// Always-on incoming-call listener. Logs the org's shared Plivo SIP endpoint
// in once (on mount) and keeps it registered so Plivo can ring the browser on
// inbound calls. Renders a centered, full-screen incoming-call modal (with a
// ringtone) when a call arrives, then a compact in-call panel until it ends.
// All media + bridging stay on Plivo's side — the browser only answers the SIP
// leg.
//
// Coexistence with outbound: the outbound PlivoBrowserCallWidget logs in its
// own client per call. To avoid two registrations fighting over one endpoint,
// this listener pauses (logs out) while an outbound call is active and resumes
// when it ends — driven by the `paused` prop from ActiveCallProvider.

import React, { useEffect, useRef, useState, useCallback } from "react";
import { PhoneIncoming, PhoneOff, Phone, Mic, MicOff, User as UserIcon, Minus, Maximize2 } from "lucide-react";
import toast from "react-hot-toast";
import { fetchPlivoListenerCredentials } from "../../hooks/useLeads";
import { useMyCrmRole } from "../../hooks/useTeamMembers";
import { useAuth } from "../../../../contexts/AuthContext";
import { useSelectedProject } from "../../contexts/SelectedProjectProvider";
import useRingtone from "../../hooks/useRingtone";

const RINGING = "ringing";
const IN_CALL = "in-call";

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

// Plivo delivers our X-PH-* sipHeaders to onIncomingCall as the second arg.
// Values were URI-encoded server-side. Shape varies across SDK versions
// (object map vs array of {name,value}), so normalise defensively.
function readHeader(extra, key) {
  if (!extra) return "";
  const want = key.toLowerCase();
  let raw = "";
  if (Array.isArray(extra)) {
    const hit = extra.find((h) => String(h?.name || "").toLowerCase() === want);
    raw = hit?.value ?? "";
  } else if (typeof extra === "object") {
    for (const k of Object.keys(extra)) {
      if (k.toLowerCase() === want) { raw = extra[k]; break; }
    }
  }
  try { return decodeURIComponent(String(raw)); } catch { return String(raw); }
}

function initialsFor(name, number) {
  const src = (name || "").trim();
  if (src) {
    const parts = src.split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
  }
  // Fall back to last 2 digits of the number.
  const digits = String(number || "").replace(/\D/g, "");
  return digits.slice(-2) || "#";
}

export default function IncomingCallListener({ paused = false }) {
  // null = idle (no incoming), "ringing" = modal, "in-call" = connected.
  const [phase, setPhase] = useState(null);
  const [callerName, setCallerName] = useState("");
  const [callerNumber, setCallerNumber] = useState("");
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  // Admins may collapse the full-screen ringing modal to a floating pill so
  // it doesn't block the screen while they finish what they're doing.
  const [minimized, setMinimized] = useState(false);
  // Which project the incoming call belongs to (from SIP headers). Drives who
  // sees it: only users on this project (or admins) get a UI.
  const [callProjectId, setCallProjectId] = useState("");
  const [callProjectName, setCallProjectName] = useState("");

  const phoneRef = useRef(null);
  const timerRef = useRef(null);
  const startedAtRef = useRef(null);
  const ringtone = useRingtone();

  // "Admin" = CRM owner/manager OR a TG host org admin. Admins may minimize and
  // also see calls for projects other than the one they're currently viewing.
  const { isAdmin: isCrmAdmin } = useMyCrmRole();
  const { user } = useAuth();
  const { projectId: selectedProjectId } = useSelectedProject();
  const isHostAdmin = user?.role === "org_admin" || user?.role === "main_admin";
  const isAdmin = isCrmAdmin || isHostAdmin;

  // Event handlers fire from the Plivo SDK callback — read the latest admin /
  // selected-project values via refs so we never decide on a stale closure.
  const isAdminRef = useRef(isAdmin);
  const selectedProjectRef = useRef(selectedProjectId);
  useEffect(() => { isAdminRef.current = isAdmin; }, [isAdmin]);
  useEffect(() => { selectedProjectRef.current = selectedProjectId; }, [selectedProjectId]);

  const clearTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const resetIdle = useCallback(() => {
    clearTimer();
    ringtone.stop();
    setPhase(null);
    setCallerName("");
    setCallerNumber("");
    setMuted(false);
    setDuration(0);
    setMinimized(false);
    setCallProjectId("");
    setCallProjectName("");
  }, [ringtone]);

  // Login lifecycle. Re-runs when `paused` flips: when paused we tear the
  // client down (freeing the endpoint for the outbound widget); when unpaused
  // we log back in to resume listening.
  useEffect(() => {
    if (paused) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const PlivoMod = await import("plivo-browser-sdk");
        const Plivo = PlivoMod.Plivo || PlivoMod.default;
        const creds = await fetchPlivoListenerCredentials();
        if (cancelled) return;

        const phone = new Plivo({
          debug: "WARN",
          permOnClick: true,
          codecs: ["OPUS", "PCMU"],
          enableIPV6: false,
          audioConstraints: { optional: [{ googAutoGainControl: false }] },
          dscp: true,
          enableTracking: true,
        });
        phoneRef.current = phone;

        phone.client.on("onLoginFailed", (cause) => {
          if (!cancelled) console.warn(`[plivo-listener] login failed: ${cause}`);
        });

        // Incoming call arrives over the SDK's own WebSocket to Plivo.
        phone.client.on("onIncomingCall", (callerId, extraHeaders) => {
          if (cancelled) return;
          const name = readHeader(extraHeaders, "X-PH-leadName");
          const fromNum = readHeader(extraHeaders, "X-PH-fromNumber") || callerId || "";
          const projId = readHeader(extraHeaders, "X-PH-projectId");
          const projName = readHeader(extraHeaders, "X-PH-projectName");
          setCallerName(name);
          setCallerNumber(fromNum || "Unknown");
          setCallProjectId(projId);
          setCallProjectName(projName);
          setMinimized(false); // always pop the full modal for a new call
          setPhase(RINGING);

          // Only ring if this user should actually see the call: they're on
          // the call's project, OR they're an admin (admins see every project,
          // as a pill if it's not the one they're viewing). Off-project
          // non-admins get no ring and no UI — the call keeps ringing the
          // shared endpoint for whoever is on that project.
          const onCallProject = !projId || String(projId) === String(selectedProjectRef.current || "");
          if (onCallProject || isAdminRef.current) {
            ringtone.start();
          }
        });

        phone.client.on("onCallAnswered", () => {
          if (cancelled) return;
          ringtone.stop();
          setPhase(IN_CALL);
          startedAtRef.current = Date.now();
          clearTimer();
          timerRef.current = setInterval(() => {
            setDuration(Math.floor((Date.now() - startedAtRef.current) / 1000));
          }, 1000);
        });

        const handleEnd = () => { if (!cancelled) resetIdle(); };
        phone.client.on("onCallTerminated", handleEnd);
        phone.client.on("onCallEnded", handleEnd);
        phone.client.on("onCallCancelled", handleEnd);
        phone.client.on("onCallRejected", handleEnd);
        phone.client.on("onCallFailed", handleEnd);
        phone.client.on("onMediaDisconnected", handleEnd);

        phone.client.login(creds.username, creds.password);
      } catch (err) {
        if (!cancelled) console.warn(`[plivo-listener] init failed: ${err?.message || err}`);
      }
    })();

    return () => {
      cancelled = true;
      clearTimer();
      ringtone.stop();
      try { if (phoneRef.current?.client?.callSession) phoneRef.current.client.hangup(); } catch {}
      try { phoneRef.current?.client?.logout(); } catch {}
      phoneRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  const accept = () => {
    ringtone.stop();
    try { phoneRef.current?.client?.answer(); } catch {
      toast.error("Could not answer call");
    }
  };

  const reject = () => {
    try { phoneRef.current?.client?.reject(); } catch {}
    resetIdle();
  };

  const hangup = () => {
    try { if (phoneRef.current?.client?.callSession) phoneRef.current.client.hangup(); } catch {}
    resetIdle();
  };

  const toggleMute = () => {
    const phone = phoneRef.current;
    if (!phone) return;
    const next = !muted;
    try {
      if (next) phone.client.mute(); else phone.client.unmute();
      setMuted(next);
    } catch {}
  };

  // While paused, the outbound widget owns the shared #plivo-remote-audio
  // element — render nothing so there's only ever one element with that id.
  if (paused) return null;

  // The SDK pipes inbound audio into this element. Mounted whenever the
  // listener is active so it's ready the instant a call is answered.
  const audioEl = <audio id="plivo-remote-audio" autoPlay style={{ display: "none" }} />;

  if (!phase) return audioEl;

  // Project scoping. A call "belongs" to callProjectId. Show rules:
  //   - on the call's project  → everyone on it sees the full modal
  //   - admin, different project → pill (oversight, can still accept)
  //   - non-admin, different project → nothing (call keeps ringing the
  //     shared endpoint for whoever is actually on that project)
  const onCallProject = !callProjectId || String(callProjectId) === String(selectedProjectId || "");
  if (!onCallProject && !isAdmin) {
    return audioEl; // off-project non-admin: invisible
  }
  // Off-project admins are forced into the pill so the call they're viewing /
  // working isn't covered by a full-screen modal for another project.
  const showAsPill = minimized || (!onCallProject && isAdmin);

  const displayName = callerName || callerNumber;
  const subtitle = callerName ? callerNumber : "";
  const initials = initialsFor(callerName, callerNumber);

  // ---- Ringing, pill (admin minimized, or admin on a different project) -----
  if (phase === RINGING && showAsPill) {
    // Off-project pill names the project so the admin knows where it's landing.
    // Expanding only makes sense when the call IS the current project (manual
    // minimize) — for another project we keep it a pill (accept connects in
    // place; no need to switch projects).
    const canExpand = onCallProject;
    const eyebrowText = onCallProject
      ? "Incoming · ringing"
      : `Incoming · ${callProjectName || "another project"}`;
    return (
      <>
        {audioEl}
        <style>{KEYFRAMES}</style>
        <div style={pillShell} role="dialog" aria-label="Incoming call (minimized)">
          <button
            onClick={canExpand ? () => setMinimized(false) : undefined}
            title={canExpand ? "Expand" : undefined}
            style={{ ...pillInfoBtn, cursor: canExpand ? "pointer" : "default" }}
          >
            <span style={pillRingIcon}>
              <PhoneIncoming size={16} />
            </span>
            <span style={{ minWidth: 0, textAlign: "left" }}>
              <span style={pillEyebrow}>{eyebrowText}</span>
              <span style={pillName}>{displayName}</span>
            </span>
            {canExpand && <Maximize2 size={13} style={{ opacity: 0.5, flexShrink: 0 }} />}
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={reject} title="Decline" style={pillBtn("var(--crm-danger, #ef4444)")}>
              <PhoneOff size={16} />
            </button>
            <button onClick={accept} title="Accept" style={pillBtn("var(--crm-success, #16a34a)")}>
              <Phone size={16} />
            </button>
          </div>
        </div>
      </>
    );
  }

  // ---- Ringing: full-screen centered modal ----------------------------------
  if (phase === RINGING) {
    return (
      <>
        {audioEl}
        <style>{KEYFRAMES}</style>
        <div style={overlay} role="dialog" aria-modal="true" aria-label="Incoming call">
          <div style={ringCard}>
            <div style={topRow}>
              <div style={eyebrowRow}>
                <span style={liveDot} />
                <PhoneIncoming size={13} />
                Incoming call
              </div>
              {isAdmin && (
                <button
                  onClick={() => setMinimized(true)}
                  title="Minimize"
                  aria-label="Minimize"
                  style={minimizeBtn}
                >
                  <Minus size={16} />
                </button>
              )}
            </div>

            <div style={avatarWrap}>
              <span style={{ ...pulseRing, animationDelay: "0s" }} />
              <span style={{ ...pulseRing, animationDelay: "0.8s" }} />
              <div style={avatar}>
                {callerName
                  ? <span style={{ fontSize: 30, fontWeight: 600 }}>{initials}</span>
                  : <UserIcon size={40} />}
              </div>
            </div>

            <div style={{ textAlign: "center", marginTop: 22 }}>
              <div style={callerNameStyle}>{displayName}</div>
              {subtitle && <div style={callerSubStyle}>{subtitle}</div>}
              {!callerName && (
                <div style={unknownTag}>Unknown caller</div>
              )}
            </div>

            <div style={actionRow}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <button onClick={reject} title="Decline" style={bigBtn("var(--crm-danger, #ef4444)")}>
                  <PhoneOff size={26} />
                </button>
                <span style={btnLabel}>Decline</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <button onClick={accept} title="Accept" style={{ ...bigBtn("var(--crm-success, #16a34a)"), animation: "tg-accept-bounce 1.4s ease-in-out infinite" }}>
                  <Phone size={26} />
                </button>
                <span style={btnLabel}>Accept</span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ---- In call: compact bottom-left panel -----------------------------------
  return (
    <>
      {audioEl}
      <style>{KEYFRAMES}</style>
      <div style={inCallShell} role="dialog" aria-label="Active call">
        <div style={inCallHeader}>
          <div style={inCallEyebrow}>
            <span style={liveDot} />
            In call
          </div>
          <div style={{ marginTop: 4, fontWeight: 600, fontSize: 16, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {displayName}
          </div>
          <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            {formatDuration(duration)}
          </div>
        </div>
        <div style={{ padding: "18px 16px", display: "flex", alignItems: "center", justifyContent: "center", gap: 16 }}>
          <button
            onClick={toggleMute}
            title={muted ? "Unmute" : "Mute"}
            style={smallBtn(
              muted ? "var(--crm-danger-soft)" : "var(--color-bg-muted)",
              muted ? "var(--crm-danger)" : "var(--color-text-primary)",
            )}
          >
            {muted ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
          <button onClick={hangup} title="Hang up" style={smallBtn("var(--crm-danger, #ef4444)", "#fff")}>
            <PhoneOff size={18} />
          </button>
        </div>
      </div>
    </>
  );
}

/* ----------------------------------------------------------------------------
 * Styles
 * ------------------------------------------------------------------------- */

const KEYFRAMES = `
@keyframes tg-pulse-ring {
  0%   { transform: scale(0.85); opacity: 0.55; }
  70%  { transform: scale(1.6);  opacity: 0; }
  100% { transform: scale(1.6);  opacity: 0; }
}
@keyframes tg-accept-bounce {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
}
@keyframes tg-overlay-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes tg-card-in {
  from { opacity: 0; transform: translateY(12px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes tg-live-dot {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.3; }
}
@keyframes tg-pill-ring {
  0%, 100% { transform: rotate(0deg); }
  20%      { transform: rotate(-14deg); }
  40%      { transform: rotate(14deg); }
  60%      { transform: rotate(-8deg); }
  80%      { transform: rotate(8deg); }
}
`;

const overlay = {
  position: "fixed", inset: 0, zIndex: 10000,
  background: "rgba(15, 23, 42, 0.55)",
  backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
  display: "flex", alignItems: "center", justifyContent: "center",
  animation: "tg-overlay-in 160ms ease-out",
  padding: 20,
};

const ringCard = {
  width: 360, maxWidth: "100%",
  background: "var(--color-bg-surface, #fff)",
  borderRadius: 24,
  border: "1px solid var(--color-border-subtle, rgba(0,0,0,0.08))",
  boxShadow: "0 40px 120px rgba(0,0,0,0.45)",
  padding: "28px 24px 24px",
  color: "var(--color-text-primary)",
  display: "flex", flexDirection: "column", alignItems: "center",
  animation: "tg-card-in 220ms cubic-bezier(0.16, 1, 0.3, 1)",
};

const topRow = {
  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
};

const eyebrowRow = {
  display: "flex", alignItems: "center", gap: 7,
  fontSize: 11, fontWeight: 600, color: "var(--crm-primary)",
};

const minimizeBtn = {
  width: 30, height: 30, borderRadius: 9999, border: "none",
  background: "var(--color-bg-muted)", color: "var(--color-text-secondary)",
  display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer",
};

const liveDot = {
  width: 8, height: 8, borderRadius: 9999,
  background: "var(--crm-success, #16a34a)",
  boxShadow: "0 0 0 3px var(--crm-success-soft, rgba(22,163,74,0.2))",
  animation: "tg-live-dot 1.2s ease-in-out infinite",
  display: "inline-block",
};

const avatarWrap = {
  position: "relative", marginTop: 26,
  width: 112, height: 112,
  display: "flex", alignItems: "center", justifyContent: "center",
};

const pulseRing = {
  position: "absolute", inset: 0, borderRadius: 9999,
  background: "var(--crm-primary-soft, rgba(99,102,241,0.25))",
  animation: "tg-pulse-ring 1.6s ease-out infinite",
};

const avatar = {
  position: "relative", zIndex: 1,
  width: 96, height: 96, borderRadius: 9999,
  background: "linear-gradient(135deg, var(--crm-primary), var(--crm-primary-strong, var(--crm-primary)))",
  color: "#fff",
  display: "flex", alignItems: "center", justifyContent: "center",
  boxShadow: "0 12px 30px var(--crm-primary-soft, rgba(99,102,241,0.4))",
};

const callerNameStyle = {
  fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em",
  color: "var(--color-text-primary)",
  maxWidth: 300, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
};

const callerSubStyle = {
  marginTop: 4, fontSize: 14,
  color: "var(--color-text-secondary)",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
};

const unknownTag = {
  marginTop: 8, display: "inline-block",
  fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
  color: "var(--color-text-secondary)",
  background: "var(--color-bg-muted)",
  padding: "3px 10px", borderRadius: 999,
};

const actionRow = {
  marginTop: 30, display: "flex", alignItems: "flex-start",
  justifyContent: "center", gap: 56, width: "100%",
};

const bigBtn = (bg) => ({
  width: 64, height: 64, borderRadius: 9999, border: "none",
  display: "flex", alignItems: "center", justifyContent: "center",
  background: bg, color: "#fff", cursor: "pointer",
  boxShadow: "0 10px 26px rgba(0,0,0,0.25)",
  transition: "transform 100ms ease, filter 100ms ease",
});

const btnLabel = {
  fontSize: 12, fontWeight: 600,
  color: "var(--color-text-secondary)",
};

const inCallShell = {
  position: "fixed", bottom: 24, left: 24, zIndex: 9999, width: 300,
  background: "var(--color-bg-surface)",
  border: "1px solid var(--color-border-subtle)",
  borderRadius: 8,
  boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
  overflow: "hidden",
  color: "var(--color-text-primary)",
  animation: "tg-card-in 200ms ease-out",
};

const inCallHeader = {
  padding: "14px 16px",
  background: "linear-gradient(135deg, var(--crm-primary), var(--crm-primary-strong, var(--crm-primary)))",
  color: "#fff",
};

const inCallEyebrow = {
  display: "flex", alignItems: "center", gap: 6,
  fontSize: 10, fontWeight: 600, opacity: 0.95,
};

const smallBtn = (bg, color) => ({
  width: 48, height: 48, borderRadius: 9999, border: "none",
  display: "flex", alignItems: "center", justifyContent: "center",
  background: bg, color, cursor: "pointer",
});

// ---- Minimized ringing pill (bottom-right) --------------------------------
const pillShell = {
  position: "fixed", bottom: 24, right: 24, zIndex: 10000,
  display: "flex", alignItems: "center", gap: 12,
  padding: "10px 12px",
  background: "var(--color-bg-surface, #fff)",
  border: "1px solid var(--color-border-subtle, rgba(0,0,0,0.08))",
  borderRadius: 9999,
  boxShadow: "0 18px 50px rgba(0,0,0,0.3)",
  color: "var(--color-text-primary)",
  maxWidth: 340,
  animation: "tg-card-in 200ms ease-out",
};

const pillInfoBtn = {
  display: "flex", alignItems: "center", gap: 10,
  background: "transparent", border: "none", cursor: "pointer",
  padding: 0, minWidth: 0, color: "inherit",
};

const pillRingIcon = {
  width: 36, height: 36, borderRadius: 9999, flexShrink: 0,
  background: "var(--crm-primary-soft, rgba(99,102,241,0.18))",
  color: "var(--crm-primary)",
  display: "flex", alignItems: "center", justifyContent: "center",
  animation: "tg-pill-ring 1s ease-in-out infinite",
};

const pillEyebrow = {
  display: "block", fontSize: 9, fontWeight: 600,
  color: "var(--crm-primary)",
};

const pillName = {
  display: "block", fontSize: 14, fontWeight: 600,
  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  maxWidth: 170,
};

const pillBtn = (bg) => ({
  width: 38, height: 38, borderRadius: 9999, border: "none",
  display: "flex", alignItems: "center", justifyContent: "center",
  background: bg, color: "#fff", cursor: "pointer", flexShrink: 0,
});
